import { useState, useCallback } from 'react';
import { 
  estadisticasService, 
  EstadisticasCompletas,
  EstadisticasJugadorEquipo
} from '@/services/EstadisticasService';

/**
 * Hook para gestionar las estadísticas del jugador
 */
export const useEstadisticas = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [estadisticas, setEstadisticas] = useState<EstadisticasCompletas | null>(null);
  const [estadisticasEquipo, setEstadisticasEquipo] = useState<EstadisticasJugadorEquipo[]>([]);

  /**
   * Obtiene todas las estadísticas de un jugador
   * @param jugadorId ID del jugador
   * @param temporadaId ID de la temporada
   * @param equipoNombre Nombre del equipo
   */
  const obtenerEstadisticas = useCallback(async (
    jugadorId: number, 
    temporadaId: number, 
    equipoNombre: string
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const estadisticasCompletas = await estadisticasService.obtenerEstadisticasCompletas(
        jugadorId, 
        temporadaId, 
        equipoNombre
      );
      setEstadisticas(estadisticasCompletas);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al obtener estadísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene estadísticas de entrenamiento para un jugador
   */
  const obtenerEstadisticasEntrenamiento = useCallback(async (
    jugadorId: number, 
    temporadaId: number
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const estadisticasEntrenamiento = await estadisticasService.obtenerEstadisticasEntrenamiento(
        jugadorId, 
        temporadaId
      );
      // Actualizar solo la parte de entrenamiento
      setEstadisticas(prev => prev ? { ...prev, entrenamiento: estadisticasEntrenamiento } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al obtener estadísticas de entrenamiento');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene las fechas específicas de faltas de un jugador
   */
  const obtenerFechasFaltas = async (jugadorId: number, temporadaId: number) => {
    try {
      setLoading(true);
      const fechas = await estadisticasService.obtenerFechasFaltas(jugadorId, temporadaId);
      return fechas;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Obtiene estadísticas de todos los jugadores de un equipo
   * @param equipoId ID del equipo
   * @param temporadaId ID de la temporada
   */
  const obtenerEstadisticasEquipo = useCallback(async (
    equipoId: number,
    temporadaId: number
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const estadisticas = await estadisticasService.obtenerEstadisticasEquipo(
        equipoId,
        temporadaId
      );
      setEstadisticasEquipo(estadisticas);
      return estadisticas;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al obtener estadísticas del equipo');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene evolución física del jugador
   */
  const obtenerEvolucionFisica = useCallback(async (jugadorId: number) => {
    setLoading(true);
    setError(null);
    
    try {
      const evolucionFisica = await estadisticasService.obtenerEvolucionFisica(jugadorId);
      // Actualizar solo la parte de evolución física
      setEstadisticas(prev => prev ? { ...prev, evolucionFisica } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al obtener evolución física');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Limpia las estadísticas
   */
  const limpiarEstadisticas = useCallback(() => {
    setEstadisticas(null);
    setError(null);
  }, []);

  /**
   * Alias para obtenerEstadisticas (compatibilidad con ModalDetalleJugador)
   */
  const obtenerEstadisticasCompletas = useCallback(async (
    jugadorId: number, 
    temporadaId?: number, 
    equipoNombre?: string
  ) => {
    // Usar valores por defecto si no se proporcionan
    const tempId = temporadaId || 1; // Valor por defecto
    const equipo = equipoNombre || 'Equipo'; // Valor por defecto
    
    return obtenerEstadisticas(jugadorId, tempId, equipo);
  }, [obtenerEstadisticas]);

  return {
    estadisticas,
    estadisticasEquipo,
    loading,
    error,
    obtenerEstadisticas,
    obtenerEstadisticasEquipo,
    obtenerEstadisticasEntrenamiento,
    obtenerEvolucionFisica,
    obtenerEstadisticasCompletas,
    limpiarEstadisticas,
    obtenerFechasFaltas
  };
};